<script>
import { Overlay } from '../../'
import { extractVTConfidenceSignals } from './vt_confidence_engine.js'

export default {
    name: 'VTConfidence',
    props: [
        'data', 'sub', 'tf'
    ],
    mixins: [Overlay],
    computed: {
        sett() {
            return this.$props.settings || {}
        },
        sma_length() {
            return this.sett.smaLength || 50
        },
        dc_length() {
            return this.sett.dcLength || 20
        },
        risk_reward_ratio() {
            return this.sett.riskRewardRatio || 3
        },
        label_offset() {
            return this.sett.labelOffset || 18
        },
        show_direction() {
            return this.sett.showDirection !== false
        },
        score_font() {
            return this.sett.font || ('11px ' + this.$props.font.split('px').pop())
        },
        min_score() {
            return this.sett.minScore || 20
        },
        max_score() {
            return this.sett.maxScore || 100
        },
        high_color() {
            return this.sett.highColor || 'rgba(38, 166, 154, 0.92)'
        },
        medium_color() {
            return this.sett.mediumColor || 'rgba(255, 193, 7, 0.92)'
        },
        low_color() {
            return this.sett.lowColor || 'rgba(239, 83, 80, 0.92)'
        },
        text_color() {
            return this.sett.textColor || '#ffffff'
        },
        anchor_color() {
            return this.sett.anchorColor || 'rgba(120, 130, 150, 0.65)'
        }
    },
    methods: {
        meta_info() {
            return {
                author: 'Mano',
                version: '1.0.0',
                desc: 'Scores every VT Target signal on the selected timeframe',
                group: 'VT',
                preset: {
                    name: 'VT Confidence',
                    side: 'onchart',
                    settings: {
                        smaLength: 50,
                        dcLength: 20,
                        riskRewardRatio: 3,
                        showDirection: true,
                        labelOffset: 18,
                        'z-index': 6
                    }
                }
            }
        },
        use_for() {
            return ['VTConfidence']
        },
        get_candles() {
            const sub = this.$props.sub
            if (sub && Array.isArray(sub.full_data) && sub.full_data.length) {
                return sub.full_data
            }
            if (Array.isArray(sub) && sub.length) {
                return sub
            }
            return Array.isArray(this.$props.data) ? this.$props.data : []
        },
        draw(ctx) {
            const candles = this.get_candles()
            if (!candles.length) return

            const layout = this.$props.layout
            const signals = extractVTConfidenceSignals(candles, {
                smaLength: this.sma_length,
                dcLength: this.dc_length,
                riskRewardRatio: this.risk_reward_ratio,
                minScore: this.min_score,
                maxScore: this.max_score
            })

            ctx.save()
            ctx.font = this.score_font
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'

            for (const signal of signals) {
                const x = Math.round(layout.t2screen(signal.date))
                if (x < -80 || x > layout.width + 80) continue
                this.draw_score(ctx, layout, signal, x)
            }

            ctx.restore()
        },
        draw_score(ctx, layout, signal, x) {
            const isBuy = signal.entryDirection === 'BUY'
            const score = Math.round(signal.confidence || 0)
            const label = this.show_direction
                ? `${isBuy ? 'B' : 'S'} ${score}%`
                : `${score}%`
            const fill = this.score_color(score)
            const textWidth = ctx.measureText(label).width
            const width = Math.max(34, textWidth + 12)
            const height = 18
            const anchorY = isBuy
                ? layout.$2screen(signal.low)
                : layout.$2screen(signal.high)
            const y = isBuy
                ? anchorY + this.label_offset
                : anchorY - this.label_offset - height
            const left = clamp(x - width / 2, 2, Math.max(2, layout.width - width - 2))

            ctx.beginPath()
            ctx.strokeStyle = this.anchor_color
            ctx.lineWidth = 1
            ctx.moveTo(x, anchorY)
            ctx.lineTo(x, isBuy ? y : y + height)
            ctx.stroke()

            ctx.beginPath()
            ctx.arc(x, anchorY, 2, 0, Math.PI * 2)
            ctx.fillStyle = fill
            ctx.fill()

            this.round_rect(ctx, left, y, width, height, 5)
            ctx.fillStyle = fill
            ctx.fill()

            ctx.fillStyle = this.text_color
            ctx.fillText(label, left + width / 2, y + height / 2 + 0.5)
        },
        score_color(score) {
            if (score >= 75) return this.high_color
            if (score >= 55) return this.medium_color
            return this.low_color
        },
        round_rect(ctx, x, y, width, height, radius) {
            const r = Math.min(radius, width / 2, height / 2)
            ctx.beginPath()
            ctx.moveTo(x + r, y)
            ctx.lineTo(x + width - r, y)
            ctx.quadraticCurveTo(x + width, y, x + width, y + r)
            ctx.lineTo(x + width, y + height - r)
            ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height)
            ctx.lineTo(x + r, y + height)
            ctx.quadraticCurveTo(x, y + height, x, y + height - r)
            ctx.lineTo(x, y + r)
            ctx.quadraticCurveTo(x, y, x + r, y)
            ctx.closePath()
        },
        legend() {
            return []
        }
    }
}

function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value))
}
</script>
